import {
	BitbucketExtension,
	type BitbucketExtensionType,
	type ForgeUIExtensionType,
} from '@atlassian/forge-ui-types/extension';

import { ensureURLPath } from '../createMakeProductURLPath';
import { ensurePropertyExists, NavigationError } from './common';

type WorkspaceNavigationParams = {
	workspaceId: string;
};

type ProjectNavigationParams = {
	projectKey: string;
	workspaceId: string;
};

type RepositoryNavigationParams = WorkspaceNavigationParams & { repositoryId: string };

const getExtensionParams = (
	extension: ForgeUIExtensionType,
): { appId: string; environmentKey: string; key: string } => {
	const { appId, environmentKey, key } = extension;
	if (!appId) {
		throw NavigationError.MissingProperty('appId');
	}
	if (!environmentKey) {
		throw NavigationError.MissingProperty('environmentKey');
	}
	if (!key) {
		throw NavigationError.MissingProperty('key');
	}

	return { appId, environmentKey, key };
};

export const bitbucketExtensionURLHelpers: Partial<
	Record<BitbucketExtensionType, (extension: ForgeUIExtensionType, params: any) => string | null>
> = {
	[BitbucketExtension.REPOSITORY_MAIN_MENU_PAGE]: (
		extension,
		{ workspaceId, repositoryId }: RepositoryNavigationParams,
	) => {
		const { appId, environmentKey, key } = getExtensionParams(extension);

		ensurePropertyExists('workspaceId', workspaceId);
		ensurePropertyExists('repositoryId', repositoryId);

		return ensureURLPath(`/${workspaceId}/${repositoryId}/forge/${appId}/${environmentKey}/${key}`);
	},
	[BitbucketExtension.REPOSITORY_SETTINGS_MENU_PAGE]: (
		extension,
		{ workspaceId, repositoryId }: RepositoryNavigationParams,
	) => {
		const { appId, environmentKey, key } = getExtensionParams(extension);

		ensurePropertyExists('workspaceId', workspaceId);
		ensurePropertyExists('repositoryId', repositoryId);

		return ensureURLPath(
			`/${workspaceId}/${repositoryId}/admin/forge/${appId}/${environmentKey}/${key}`,
		);
	},
	[BitbucketExtension.PROJECT_SETTINGS_MENU_PAGE]: (
		extension,
		{ workspaceId, projectKey }: ProjectNavigationParams,
	) => {
		const { appId, environmentKey, key } = getExtensionParams(extension);

		ensurePropertyExists('workspaceId', workspaceId);
		ensurePropertyExists('projectKey', projectKey);

		return ensureURLPath(
			`/${workspaceId}/workspace/projects/${projectKey}/settings/forge/${appId}/${environmentKey}/${key}`,
		);
	},
	[BitbucketExtension.WORKSPACE_GLOBAL_PAGE]: (
		extension,
		{ workspaceId }: WorkspaceNavigationParams,
	) => {
		const { appId, environmentKey, key } = getExtensionParams(extension);

		ensurePropertyExists('workspaceId', workspaceId);

		return ensureURLPath(`/${workspaceId}/workspace/forge/${appId}/${environmentKey}/${key}`);
	},
	[BitbucketExtension.WORKSPACE_PERSONAL_SETTINGS_PAGE]: (
		extension,
		{ workspaceId }: WorkspaceNavigationParams,
	) => {
		const { appId, environmentKey, key } = getExtensionParams(extension);

		ensurePropertyExists('workspaceId', workspaceId);

		return ensureURLPath(
			`/${workspaceId}/workspace/personal-settings/forge/${appId}/${environmentKey}/${key}`,
		);
	},
	[BitbucketExtension.WORKSPACE_SETTINGS_MENU_PAGE]: (
		extension,
		{ workspaceId }: WorkspaceNavigationParams,
	) => {
		const { appId, environmentKey, key } = getExtensionParams(extension);

		ensurePropertyExists('workspaceId', workspaceId);

		return ensureURLPath(
			`/${workspaceId}/workspace/settings/forge/${appId}/${environmentKey}/${key}`,
		);
	},
};
